/**
 * SPIN TENET — Normalized CFD Simulation Result & Provenance Contract (Gate 3.1)
 *
 * Defines the authoritative, solver-neutral data contract for CFD results.
 * Guarantees scientific traceability from remote solver (OpenFOAM) -> normalized result -> probe.
 *
 * SCIENTIFIC INTEGRITY AXIOMS:
 * 1. NO FAKE CFD: A mock, surrogate, or synthetic result must NEVER have fieldOrigin = 'AUTHORITATIVE_CFD'.
 * 2. Units are strictly SI: positions in meters (m), velocities in meters per second (m/s).
 * 3. Vector data locations are explicitly recorded ('CELL_CENTERS', 'MESH_NODES', 'UNIFORM_PROBE_GRID', or 'UNKNOWN').
 * 4. Zero velocity normalization or scaling in data pipeline; underlying velocity values are preserved 1:1.
 * 5. Nearest-sample probe semantics: probes return actual stored CFD sample, no artificial interpolation.
 */

import type { EngineId } from './engine';

export const CFD_RESULT_VERSION = '1.0.0';

export type FieldOrigin =
  | 'AUTHORITATIVE_CFD'     // Rigorous Navier-Stokes / LBM solver output from remote engine
  | 'INTERACTIVE_PHYSICAL'  // Reduced-order physical approximation
  | 'SURROGATE'             // Machine learning / surrogate model inference
  | 'VISUAL_ONLY'           // Non-physical decorative visualizer
  | 'MOCK_RESULT';          // Synthetic test vectors for developer harness only

export type VelocityDataLocation =
  | 'CELL_CENTERS'          // Values sampled at polyhedral/cartesian cell centroids
  | 'MESH_NODES'            // Values interpolated to computational grid vertices
  | 'UNIFORM_PROBE_GRID'    // Values extracted onto uniform virtual probe grid
  | 'UNKNOWN';              // Data location cannot be established from extraction

export interface VelocityFieldContract {
  /** Explicit physical location type of the sampled vectors. */
  dataLocation: VelocityDataLocation;

  /** Number of 3D vector points. */
  count: number;

  /** Physical SI units contract. */
  units: {
    position: 'm';
    velocity: 'm/s';
  };

  /** Reference incoming wind condition associated with this result. */
  reference?: {
    speed: number;
    direction: number;
  };

  /**
   * Flattened positions array [x0, y0, z0, x1, y1, z1, ...].
   * Coordinate system: local Cartesian metres. Length = count * 3.
   */
  positions: number[];

  /**
   * Flattened velocity vectors array [u0, v0, w0, u1, v1, w1, ...].
   * Physical SI units: m/s. Length = count * 3.
   */
  vectors: number[];
}

export interface CfdResultSummary {
  resultVersion: string;
  runId: string;
  engine: EngineId;
  engineVersion: string;
  status: 'COMPLETED' | 'FAILED' | 'PENDING';
  fieldOrigin: FieldOrigin;
  coordinateSystem: string;
  pointCount: number;
  minSpeed?: number;
  maxSpeed?: number;
  meanSpeed?: number;
  dataLocation: VelocityDataLocation;
  completedAt?: string;
  provenanceSummary?: string;
}

export interface CfdResultProvenance {
  /** Identifier of the completed run. */
  runId: string;

  /** Identifier of the originating CFD engine. */
  engineId: EngineId;

  /** Specific semantic version or release of the solver engine (or null if unavailable). */
  engineVersion: string | null;

  /** Numerical solver application name (e.g. 'icoFoam', 'simpleFoam', or null if unavailable). */
  solver: string | null;

  /** Remote case directory reference (or null if unavailable). */
  casePath: string | null;

  /** Case identifier (or null if unavailable). */
  caseId: string | null;

  /** Field identifier extracted (e.g. 'U' for velocity, or null if unavailable). */
  sourceField: string | null;

  /** Solution time step or iteration extracted (e.g. '0.05', or null if unavailable). */
  sourceTime: string | number | null;

  /** ISO 8601 timestamp of result extraction (or null if unavailable). */
  resultExtractionTimestamp: string | null;

  /** Explicit spatial coordinate system (e.g. 'CARTESIAN_METRES_Y_UP', or null if unavailable). */
  coordinateSystem: string | null;

  /** Explicit geometric data location of extracted vectors. */
  dataLocation: VelocityDataLocation;

  // Supplementary adapter metadata
  adapterVersion?: string;
  executedAt?: string;
  solverHost?: string | null;
  solverVersion?: string | null;
  wallClockSeconds?: number | null;
  totalIterations?: number | null;
  converged?: boolean | null;
  notes?: string | null;
}

export interface CfdResult {
  resultVersion: string;
  runId: string;
  engine: EngineId;
  engineVersion: string;
  status: 'COMPLETED' | 'FAILED' | 'PENDING';
  coordinateSystem: string;
  domain: {
    width: number;
    length: number;
    height: number;
    bounds: {
      min: [number, number, number];
      max: [number, number, number];
    };
  };
  fieldOrigin: FieldOrigin;
  fields: {
    velocity: VelocityFieldContract;
  };
  provenance: CfdResultProvenance;
}

export interface CfdResultDiagnostics {
  runId: string;
  pointCount: number;
  magnitude: {
    min: number;
    max: number;
    mean: number;
  };
  components: {
    ux: { min: number; max: number };
    uy: { min: number; max: number };
    uz: { min: number; max: number };
  };
  calculatedAt: string;
}

export interface CfdSampleVerification {
  index: number;
  sourcePosition: [number, number, number];
  sourceVelocity: [number, number, number];
  normalizedPosition: [number, number, number];
  normalizedVelocity: [number, number, number];
  positionDelta: number;
  velocityDelta: number;
  isPreserved: boolean;
}

export interface CfdFieldProbe {
  probeId: string;
  runId: string;
  fieldOrigin: FieldOrigin;
  engineId: EngineId;
  status: 'FOUND' | 'OUTSIDE_FIELD' | 'NO_RESULT' | 'ERROR';
  clickPosition: {
    x: number;
    y: number;
    z: number;
    unit: 'm';
  };
  sample?: {
    index: number;
    sampleType: 'NEAREST_SAMPLE' | 'EXACT_SAMPLE';
    distanceM: number;
    position: {
      x: number;
      y: number;
      z: number;
      unit: 'm';
    };
    velocity: {
      ux: number;
      uy: number;
      uz: number;
      magnitude: number;
      unit: 'm/s';
    };
    source: {
      dataLocation: VelocityDataLocation;
      sourceField: string | null;
      sourceTime: string | number | null;
    };
  };
  errorMessage?: string;
  probedAt: string;
}

export type IntegrityFailureCode =
  | 'MISSING_PROVENANCE'
  | 'UNKNOWN_DATA_LOCATION'
  | 'INVALID_VECTOR_DATA'
  | 'UNIT_METADATA_MISSING'
  | 'RUN_NOT_FOUND'
  | 'RESULT_NOT_AVAILABLE'
  | 'UNSUPPORTED_PROBE';

/**
 * Computes exact deterministic magnitude of a 3D velocity vector [Ux, Uy, Uz] in m/s (Gate 4.1).
 * Formula: |U| = sqrt(Ux² + Uy² + Uz²)
 */
export function computeVelocityMagnitude(ux: number, uy: number, uz: number): number {
  if (
    typeof ux !== 'number' || typeof uy !== 'number' || typeof uz !== 'number' ||
    !Number.isFinite(ux) || !Number.isFinite(uy) || !Number.isFinite(uz)
  ) {
    throw new Error(
      `INVALID_VECTOR_DATA: Cannot compute velocity magnitude for non-finite components: [${ux}, ${uy}, ${uz}].`
    );
  }
  return Math.sqrt(ux * ux + uy * uy + uz * uz);
}

export interface DerivedVelocityMagnitudeField {
  /** Identifier of the evaluated run. */
  runId: string;
  /** Explicit provenance origin; MUST be 'AUTHORITATIVE_CFD' for authoritative analysis. */
  fieldOrigin: FieldOrigin;
  /** Originating simulation engine. */
  engineId: EngineId;
  /** SI units contract. */
  units: 'm/s';
  /** Number of vector samples. */
  count: number;
  /**
   * Deterministic derived scalar magnitude array for each vector:
   * |U| = sqrt(Ux² + Uy² + Uz²)
   * Physical SI units: m/s. Length === count.
   */
  velocityMagnitudeMps: number[];
  /** Minimum velocity magnitude across all samples (m/s). */
  minMagnitudeMps: number;
  /** Maximum velocity magnitude across all samples (m/s). */
  maxMagnitudeMps: number;
  /** Arithmetic mean velocity magnitude across all samples (m/s). */
  meanMagnitudeMps: number;
  /** ISO 8601 calculation timestamp. */
  calculatedAt: string;
}

/**
 * Deterministically derives the velocity magnitude field |U| = sqrt(Ux² + Uy² + Uz²)
 * from an authoritative CFD result (Gate 4.1).
 *
 * SCIENTIFIC INTEGRITY INVARIANTS:
 * 1. Requires fieldOrigin === 'AUTHORITATIVE_CFD' for authoritative CFD analysis.
 *    Non-authoritative results (MOCK_RESULT, VISUAL_ONLY, SURROGATE, INTERACTIVE_PHYSICAL) are rejected.
 * 2. Underlying source velocity vectors are NEVER modified, scaled, rotated, or interpolated.
 * 3. All calculations remain strictly in SI units: m/s.
 * 4. Min, max, arithmetic mean, and sample count are computed exactly.
 * 5. Invalid or non-finite vector components are rejected; no magnitude is fabricated.
 */
export function deriveVelocityMagnitudeField(
  result: CfdResult,
  options?: { requireAuthoritative?: boolean }
): DerivedVelocityMagnitudeField {
  if (!result) {
    throw new Error('RESULT_NOT_AVAILABLE: Cannot derive velocity magnitude from null or undefined result.');
  }

  const requireAuth = options?.requireAuthoritative !== false;
  if (requireAuth && result.fieldOrigin !== 'AUTHORITATIVE_CFD') {
    throw new Error(
      `SCIENTIFIC INTEGRITY VIOLATION: Authoritative velocity magnitude analysis requires fieldOrigin = 'AUTHORITATIVE_CFD', but received '${result.fieldOrigin}'. Non-authoritative results cannot be presented as authoritative CFD analysis.`
    );
  }

  const validation = validateCfdResultIntegrity(result);
  if (!validation.isValid) {
    throw new Error(
      `RESULT_INVALID: Result failed integrity validation: ${validation.errors.join('; ')}`
    );
  }

  const vel = result.fields?.velocity;
  if (!vel || !Array.isArray(vel.vectors) || !Array.isArray(vel.positions)) {
    throw new Error('INVALID_VECTOR_DATA: Velocity field contract or vectors array is missing.');
  }

  const count = vel.count;
  if (count <= 0) {
    throw new Error('INVALID_VECTOR_DATA: Velocity field contains zero vector samples.');
  }

  if (vel.vectors.length !== count * 3) {
    throw new Error(
      `INVALID_VECTOR_DATA: Vector array length (${vel.vectors.length}) does not match count * 3 (${count * 3}).`
    );
  }

  const vectors = vel.vectors;
  const velocityMagnitudeMps = new Array<number>(count);
  let minMag = Infinity;
  let maxMag = -Infinity;
  let sumMag = 0;

  for (let i = 0; i < count; i++) {
    const ux = vectors[i * 3];
    const uy = vectors[i * 3 + 1];
    const uz = vectors[i * 3 + 2];

    if (
      typeof ux !== 'number' || typeof uy !== 'number' || typeof uz !== 'number' ||
      !Number.isFinite(ux) || !Number.isFinite(uy) || !Number.isFinite(uz)
    ) {
      throw new Error(
        `INVALID_VECTOR_DATA: Non-finite velocity component encountered at sample index ${i}: [${ux}, ${uy}, ${uz}]. Cannot fabricate magnitude.`
      );
    }

    const mag = Math.sqrt(ux * ux + uy * uy + uz * uz);
    if (!Number.isFinite(mag) || isNaN(mag)) {
      throw new Error(
        `INVALID_VECTOR_DATA: Non-finite velocity magnitude calculated at sample index ${i}.`
      );
    }

    velocityMagnitudeMps[i] = mag;
    if (mag < minMag) minMag = mag;
    if (mag > maxMag) maxMag = mag;
    sumMag += mag;
  }

  return {
    runId: result.runId,
    fieldOrigin: result.fieldOrigin,
    engineId: result.engine,
    units: 'm/s',
    count,
    velocityMagnitudeMps,
    minMagnitudeMps: Number.isFinite(minMag) ? minMag : 0,
    maxMagnitudeMps: Number.isFinite(maxMag) ? maxMag : 0,
    meanMagnitudeMps: count > 0 ? sumMag / count : 0,
    calculatedAt: new Date().toISOString()
  };
}

/**
 * Calculates deterministic scientific integrity statistics from the actual velocity vectors.
 */
export function computeCfdResultDiagnostics(result: CfdResult): CfdResultDiagnostics {
  const v = result.fields?.velocity?.vectors || [];
  const count = result.fields?.velocity?.count || 0;
  if (count === 0 || v.length < 3) {
    return {
      runId: result.runId,
      pointCount: 0,
      magnitude: { min: 0, max: 0, mean: 0 },
      components: {
        ux: { min: 0, max: 0 },
        uy: { min: 0, max: 0 },
        uz: { min: 0, max: 0 }
      },
      calculatedAt: new Date().toISOString()
    };
  }

  if (result.fieldOrigin === 'AUTHORITATIVE_CFD') {
    const derived = deriveVelocityMagnitudeField(result);
    let minUx = Infinity;
    let maxUx = -Infinity;
    let minUy = Infinity;
    let maxUy = -Infinity;
    let minUz = Infinity;
    let maxUz = -Infinity;

    for (let i = 0; i < count; i++) {
      const ux = v[i * 3];
      const uy = v[i * 3 + 1];
      const uz = v[i * 3 + 2];
      if (ux < minUx) minUx = ux;
      if (ux > maxUx) maxUx = ux;
      if (uy < minUy) minUy = uy;
      if (uy > maxUy) maxUy = uy;
      if (uz < minUz) minUz = uz;
      if (uz > maxUz) maxUz = uz;
    }

    return {
      runId: result.runId,
      pointCount: count,
      magnitude: {
        min: derived.minMagnitudeMps,
        max: derived.maxMagnitudeMps,
        mean: derived.meanMagnitudeMps
      },
      components: {
        ux: { min: Number.isFinite(minUx) ? minUx : 0, max: Number.isFinite(maxUx) ? maxUx : 0 },
        uy: { min: Number.isFinite(minUy) ? minUy : 0, max: Number.isFinite(maxUy) ? maxUy : 0 },
        uz: { min: Number.isFinite(minUz) ? minUz : 0, max: Number.isFinite(maxUz) ? maxUz : 0 }
      },
      calculatedAt: derived.calculatedAt
    };
  }

  let minMag = Infinity;
  let maxMag = -Infinity;
  let sumMag = 0;
  let minUx = Infinity;
  let maxUx = -Infinity;
  let minUy = Infinity;
  let maxUy = -Infinity;
  let minUz = Infinity;
  let maxUz = -Infinity;

  for (let i = 0; i < count; i++) {
    const ux = v[i * 3];
    const uy = v[i * 3 + 1];
    const uz = v[i * 3 + 2];
    const mag = Math.sqrt(ux * ux + uy * uy + uz * uz);

    if (mag < minMag) minMag = mag;
    if (mag > maxMag) maxMag = mag;
    sumMag += mag;

    if (ux < minUx) minUx = ux;
    if (ux > maxUx) maxUx = ux;
    if (uy < minUy) minUy = uy;
    if (uy > maxUy) maxUy = uy;
    if (uz < minUz) minUz = uz;
    if (uz > maxUz) maxUz = uz;
  }

  return {
    runId: result.runId,
    pointCount: count,
    magnitude: {
      min: Number.isFinite(minMag) ? minMag : 0,
      max: Number.isFinite(maxMag) ? maxMag : 0,
      mean: count > 0 ? sumMag / count : 0
    },
    components: {
      ux: { min: Number.isFinite(minUx) ? minUx : 0, max: Number.isFinite(maxUx) ? maxUx : 0 },
      uy: { min: Number.isFinite(minUy) ? minUy : 0, max: Number.isFinite(maxUy) ? maxUy : 0 },
      uz: { min: Number.isFinite(minUz) ? minUz : 0, max: Number.isFinite(maxUz) ? maxUz : 0 }
    },
    calculatedAt: new Date().toISOString()
  };
}

/**
 * Deterministic verification of representative field samples between raw and normalized formats.
 */
export function verifyCfdSampleEquivalence(
  sourceOrResult: CfdResult | { positions: number[]; vectors: number[] },
  normalizedOrIndices?: { positions: number[]; vectors: number[] } | number[],
  sampleIndices?: number[]
): CfdSampleVerification[] {
  let source: { positions: number[]; vectors: number[] };
  let normalized: { positions: number[]; vectors: number[] };
  let indicesToUse: number[] | undefined;

  if ('fields' in sourceOrResult) {
    // Single CfdResult passed
    const vel = sourceOrResult.fields?.velocity;
    source = { positions: vel?.positions || [], vectors: vel?.vectors || [] };
    normalized = { positions: vel?.positions || [], vectors: vel?.vectors || [] };
    indicesToUse = Array.isArray(normalizedOrIndices) ? normalizedOrIndices : sampleIndices;
  } else {
    // Separate source and normalized passed
    source = sourceOrResult;
    normalized = (normalizedOrIndices && !Array.isArray(normalizedOrIndices))
      ? normalizedOrIndices
      : sourceOrResult;
    indicesToUse = sampleIndices;
  }

  const count = Math.min(
    Math.floor((source.positions?.length || 0) / 3),
    Math.floor((normalized.positions?.length || 0) / 3)
  );
  const indices = indicesToUse && indicesToUse.length > 0
    ? indicesToUse.filter((idx) => idx >= 0 && idx < count)
    : count <= 5
      ? Array.from({ length: count }, (_, i) => i)
      : [0, Math.floor(count / 4), Math.floor(count / 2), Math.floor((3 * count) / 4), count - 1];

  return indices.map((idx) => {
    const sp: [number, number, number] = [
      source.positions[idx * 3],
      source.positions[idx * 3 + 1],
      source.positions[idx * 3 + 2]
    ];
    const sv: [number, number, number] = [
      source.vectors[idx * 3],
      source.vectors[idx * 3 + 1],
      source.vectors[idx * 3 + 2]
    ];
    const np: [number, number, number] = [
      normalized.positions[idx * 3],
      normalized.positions[idx * 3 + 1],
      normalized.positions[idx * 3 + 2]
    ];
    const nv: [number, number, number] = [
      normalized.vectors[idx * 3],
      normalized.vectors[idx * 3 + 1],
      normalized.vectors[idx * 3 + 2]
    ];

    const posDelta = Math.sqrt(
      (sp[0] - np[0]) ** 2 + (sp[1] - np[1]) ** 2 + (sp[2] - np[2]) ** 2
    );
    const velDelta = Math.sqrt(
      (sv[0] - nv[0]) ** 2 + (sv[1] - nv[1]) ** 2 + (sv[2] - nv[2]) ** 2
    );

    return {
      index: idx,
      sourcePosition: sp,
      sourceVelocity: sv,
      normalizedPosition: np,
      normalizedVelocity: nv,
      positionDelta: posDelta,
      velocityDelta: velDelta,
      isPreserved: posDelta < 1e-5 && velDelta < 1e-5
    };
  });
}

/**
 * Interrogates a candidate CFD simulation result at a specified 3D location.
 * Implements strict nearest-sample lookup without synthetic interpolation.
 */
export function probeCfdResult(
  result: CfdResult | null,
  clickPoint: [number, number, number],
  targetRunId?: string,
  maxSearchDistanceM: number = 250.0
): CfdFieldProbe {
  const probeId = `probe-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
  const probedAt = new Date().toISOString();
  const clickPos = {
    x: clickPoint[0],
    y: clickPoint[1],
    z: clickPoint[2],
    unit: 'm' as const
  };

  if (!result) {
    return {
      probeId,
      runId: targetRunId || 'UNKNOWN',
      fieldOrigin: 'VISUAL_ONLY',
      engineId: 'openfoam',
      status: 'NO_RESULT',
      clickPosition: clickPos,
      errorMessage: 'RESULT_NOT_AVAILABLE: No CFD simulation result available to probe.',
      probedAt
    };
  }

  if (targetRunId && result.runId !== targetRunId) {
    return {
      probeId,
      runId: result.runId,
      fieldOrigin: result.fieldOrigin,
      engineId: result.engine,
      status: 'ERROR',
      clickPosition: clickPos,
      errorMessage: `RUN_NOT_FOUND: Active result run (${result.runId}) does not match requested probe target (${targetRunId}).`,
      probedAt
    };
  }

  // Verify domain bounding box
  const bounds = result.domain?.bounds;
  if (bounds?.min && bounds?.max) {
    const margin = 15.0; // 15m tolerance around domain envelope
    const [cx, cy, cz] = clickPoint;
    if (
      cx < bounds.min[0] - margin || cx > bounds.max[0] + margin ||
      cy < bounds.min[1] - margin || cy > bounds.max[1] + margin ||
      cz < bounds.min[2] - margin || cz > bounds.max[2] + margin
    ) {
      return {
        probeId,
        runId: result.runId,
        fieldOrigin: result.fieldOrigin,
        engineId: result.engine,
        status: 'OUTSIDE_FIELD',
        clickPosition: clickPos,
        errorMessage: 'No CFD sample available at this location.',
        probedAt
      };
    }
  }

  const pos = result.fields?.velocity?.positions;
  const vec = result.fields?.velocity?.vectors;
  const count = result.fields?.velocity?.count || 0;

  if (!pos || !vec || count === 0) {
    return {
      probeId,
      runId: result.runId,
      fieldOrigin: result.fieldOrigin,
      engineId: result.engine,
      status: 'NO_RESULT',
      clickPosition: clickPos,
      errorMessage: 'INVALID_VECTOR_DATA: Result contains zero vector samples.',
      probedAt
    };
  }

  // Nearest-sample search across physical cell centers
  let nearestIdx = -1;
  let minDistanceSq = Infinity;
  const [cx, cy, cz] = clickPoint;

  for (let i = 0; i < count; i++) {
    const px = pos[i * 3];
    const py = pos[i * 3 + 1];
    const pz = pos[i * 3 + 2];
    const dx = px - cx;
    const dy = py - cy;
    const dz = pz - cz;
    const distSq = dx * dx + dy * dy + dz * dz;

    if (distSq < minDistanceSq) {
      minDistanceSq = distSq;
      nearestIdx = i;
    }
  }

  const minDistance = Math.sqrt(minDistanceSq);
  if (nearestIdx === -1 || minDistance > maxSearchDistanceM) {
    return {
      probeId,
      runId: result.runId,
      fieldOrigin: result.fieldOrigin,
      engineId: result.engine,
      status: 'OUTSIDE_FIELD',
      clickPosition: clickPos,
      errorMessage: 'No CFD sample available at this location.',
      probedAt
    };
  }

  const ux = vec[nearestIdx * 3];
  const uy = vec[nearestIdx * 3 + 1];
  const uz = vec[nearestIdx * 3 + 2];
  const mag = computeVelocityMagnitude(ux, uy, uz);
  const sx = pos[nearestIdx * 3];
  const sy = pos[nearestIdx * 3 + 1];
  const sz = pos[nearestIdx * 3 + 2];

  return {
    probeId,
    runId: result.runId,
    fieldOrigin: result.fieldOrigin,
    engineId: result.engine,
    status: 'FOUND',
    clickPosition: clickPos,
    sample: {
      index: nearestIdx,
      sampleType: minDistance < 0.001 ? 'EXACT_SAMPLE' : 'NEAREST_SAMPLE',
      distanceM: minDistance,
      position: {
        x: sx,
        y: sy,
        z: sz,
        unit: 'm'
      },
      velocity: {
        ux,
        uy,
        uz,
        magnitude: mag,
        unit: 'm/s'
      },
      source: {
        dataLocation: result.fields.velocity.dataLocation || result.provenance?.dataLocation || 'CELL_CENTERS',
        sourceField: result.provenance?.sourceField || 'U',
        sourceTime: result.provenance?.sourceTime || null
      }
    },
    probedAt
  };
}

/**
 * Validates a candidate CfdResult against the scientific integrity rules.
 * Throws explicit failure codes if provenance is missing or data is malformed.
 */
export function validateCfdResultIntegrity(result: CfdResult): {
  isValid: boolean;
  errors: string[];
  failureCodes: IntegrityFailureCode[];
} {
  const errors: string[] = [];
  const failureCodes: IntegrityFailureCode[] = [];

  if (!result) {
    return {
      isValid: false,
      errors: ['Result object is null or undefined.'],
      failureCodes: ['RESULT_NOT_AVAILABLE']
    };
  }

  if (!result.runId) {
    errors.push('Result must possess a stable runId.');
    failureCodes.push('MISSING_PROVENANCE');
  }

  if (!result.engine) {
    errors.push('Result must identify originating simulation engine.');
    failureCodes.push('MISSING_PROVENANCE');
  }

  if (!result.provenance) {
    errors.push('MISSING_PROVENANCE: Result must contain explicit provenance metadata.');
    failureCodes.push('MISSING_PROVENANCE');
  } else {
    if (!result.provenance.runId) {
      errors.push('MISSING_PROVENANCE: Provenance must contain runId.');
      failureCodes.push('MISSING_PROVENANCE');
    }
    if (!result.provenance.engineId) {
      errors.push('MISSING_PROVENANCE: Provenance must contain engineId.');
      failureCodes.push('MISSING_PROVENANCE');
    }
  }

  const v = result.fields?.velocity;
  if (!v) {
    errors.push('Result missing velocity field contract.');
    failureCodes.push('INVALID_VECTOR_DATA');
  } else {
    if (!v.units || v.units.position !== 'm' || v.units.velocity !== 'm/s') {
      errors.push('UNIT_METADATA_MISSING: Position units must be "m" and velocity units must be "m/s".');
      failureCodes.push('UNIT_METADATA_MISSING');
    }

    if (!v.dataLocation || v.dataLocation === 'UNKNOWN') {
      if (result.fieldOrigin === 'AUTHORITATIVE_CFD') {
        errors.push('UNKNOWN_DATA_LOCATION: Authoritative CFD result must identify explicit data location (CELL_CENTERS, MESH_NODES, or UNIFORM_PROBE_GRID).');
        failureCodes.push('UNKNOWN_DATA_LOCATION');
      }
    }

    if (!Array.isArray(v.positions) || v.positions.length !== v.count * 3) {
      errors.push(`INVALID_VECTOR_DATA: Positions array length (${v.positions?.length}) does not match count * 3 (${v.count * 3}).`);
      failureCodes.push('INVALID_VECTOR_DATA');
    }

    if (!Array.isArray(v.vectors) || v.vectors.length !== v.count * 3) {
      errors.push(`INVALID_VECTOR_DATA: Vectors array length (${v.vectors?.length}) does not match count * 3 (${v.count * 3}).`);
      failureCodes.push('INVALID_VECTOR_DATA');
    }

    // Check finite numbers
    if (v.positions) {
      const checkLim = Math.min(v.positions.length, 300);
      for (let i = 0; i < checkLim; i++) {
        if (!Number.isFinite(v.positions[i])) {
          errors.push('INVALID_VECTOR_DATA: Non-finite position coordinates encountered.');
          failureCodes.push('INVALID_VECTOR_DATA');
          break;
        }
      }
    }
    if (v.vectors) {
      const checkLim = Math.min(v.vectors.length, 300);
      for (let i = 0; i < checkLim; i++) {
        if (!Number.isFinite(v.vectors[i])) {
          errors.push('INVALID_VECTOR_DATA: Non-finite velocity vector encountered.');
          failureCodes.push('INVALID_VECTOR_DATA');
          break;
        }
      }
    }
  }

  // Integrity constraint: mock results cannot masquerade as authoritative CFD
  if (result.fieldOrigin === 'AUTHORITATIVE_CFD') {
    const provNotes = (result.provenance?.notes || '').toLowerCase();
    const solverStr = (result.provenance?.solver || '').toLowerCase();
    if (
      provNotes.includes('mock') ||
      provNotes.includes('fake') ||
      provNotes.includes('synthetic') ||
      solverStr.includes('mock')
    ) {
      errors.push('VIOLATION: Mock result data cannot claim fieldOrigin = AUTHORITATIVE_CFD.');
      failureCodes.push('INVALID_VECTOR_DATA');
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    failureCodes: Array.from(new Set(failureCodes))
  };
}
